import React, { useEffect, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  ChevronDown,
  CircleHelp,
  ExternalLink,
  MapPin,
  PlayCircle,
  ShieldCheck,
  UserRound,
  Wrench,
  X,
} from "lucide-react";

const FAQS = [
  {
    question: "Why is my location not tracked while submitting attendance?",
    answer: "Turn on Location/GPS, allow this application to use your precise location, and make sure you have a stable internet connection. Then return to attendance and try again. If it still fails, restart the app or browser and contact your HR administrator with a screenshot and the time of the attempt.",
  },
  {
    question: "Where can I find my point of contact information?",
    answer: "Your reporting manager or HR point of contact is normally available in your employee profile or organisation chart. If no contact is shown, ask your HR administrator to update the reporting relationship for your employee record.",
  },
  {
    question: "How do I change my password?",
    answer: "Open your profile menu, go to Profile Settings, select Change Password, and enter your current and new passwords. Use a strong password that you do not use elsewhere. If you cannot sign in, use the Forgot Password option on the login page.",
  },
  {
    question: "How can I request services that are missing from my login?",
    answer: "Access to services is controlled by your assigned role. Send the service name and the reason you need access to your manager or HR administrator. After approval, an administrator can update your role and permissions.",
  },
  {
    question: "Why can't I see my latest attendance entry?",
    answer: "New entries can take a moment to appear. Refresh the attendance page and check that the selected date range includes today. If the entry is still missing, raise an attendance regularisation request or contact HR.",
  },
  {
    question: "How do I correct an incorrect attendance record?",
    answer: "Open Attendance, locate the affected date, and submit a regularisation request with the correct time and a short explanation. Your manager or HR team will review it according to your organisation's policy.",
  },
  {
    question: "How do I apply for leave and check its status?",
    answer: "Open Leave Management, choose Apply Leave, select the leave type and dates, and submit the request. You can follow approval progress from Leave History.",
  },
  {
    question: "What should I do if a page or feature is not loading?",
    answer: "Check your connection, refresh the page, and sign out and back in. If the issue continues, share the page name, time, browser or device, and a screenshot with your support contact.",
  },
];

const DEMO_LINKS = [
  {
    title: "Enable location access",
    description: "Learn how location permissions work on Android devices.",
    videoId: "E4WlUXrJgy4",
    icon: MapPin,
  },
  {
    title: "Create a strong password",
    description: "Helpful password-security guidance for protecting your account.",
    videoId: "3NjQ9b3pgIg",
    icon: ShieldCheck,
  },
  {
    title: "Employee self-service overview",
    description: "A sample walkthrough showing how an employee help video opens here.",
    videoId: "ScMzIvxBSi4",
    icon: UserRound,
  },
];

function VideoDialog({ video, onClose }) {
  return (
    <DialogPrimitive.Root open={Boolean(video)} onOpenChange={(open) => !open && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[70] bg-[#0a1230]/70 backdrop-blur-sm" />
        <DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-[70] w-[calc(100%-2rem)] max-w-4xl -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl bg-black shadow-2xl focus:outline-none">
          <div className="flex items-center justify-between bg-white px-4 py-3">
            <DialogPrimitive.Title className="truncate pr-4 font-semibold text-[var(--color-text-primary)]">
              {video?.title}
            </DialogPrimitive.Title>
            <DialogPrimitive.Close className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-[var(--color-text-secondary)] hover:bg-[var(--color-workspace)]" aria-label="Close video">
              <X className="h-5 w-5" />
            </DialogPrimitive.Close>
          </div>
          {video && (
            <div className="aspect-video w-full">
              <iframe
                className="h-full w-full"
                src={`https://www.youtube-nocookie.com/embed/${video.videoId}?autoplay=1&rel=0`}
                title={video.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export function FaqModal({ open, onOpenChange }) {
  const [expandedIndex, setExpandedIndex] = useState(-1);
  const [search, setSearch] = useState("");
  const [selectedVideo, setSelectedVideo] = useState(null);
  const query = search.trim().toLowerCase();
  const filteredFaqs = FAQS.map((faq, index) => ({ ...faq, index })).filter(
    (faq) => faq.question.toLowerCase().includes(query) || faq.answer.toLowerCase().includes(query)
  );

  useEffect(() => {
    if (!open) {
      setSelectedVideo(null);
      setExpandedIndex(-1);
      setSearch("");
    }
  }, [open]);

  return (
    <>
      <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-[#0a1230]/50 backdrop-blur-sm" />
          <DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-50 flex max-h-[88vh] w-[calc(100%-2rem)] max-w-3xl -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl border border-[var(--color-border)] bg-white shadow-2xl focus:outline-none">
            <div className="flex items-center justify-between border-b border-[var(--color-border)] px-5 py-4">
              <div className="flex min-w-0 items-center gap-3">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[var(--color-primary)]/10 text-[var(--color-primary)]">
                  <CircleHelp className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <DialogPrimitive.Title className="font-semibold text-[var(--color-text-primary)]">Help &amp; FAQs</DialogPrimitive.Title>
                  <DialogPrimitive.Description className="text-sm text-[var(--color-text-secondary)]">Quick answers and helpful video guides</DialogPrimitive.Description>
                </div>
              </div>
              <DialogPrimitive.Close className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-[var(--color-text-secondary)] hover:bg-[var(--color-workspace)]/60" aria-label="Close FAQs">
                <X className="h-4 w-4" />
              </DialogPrimitive.Close>
            </div>

            <div className="flex-1 overflow-y-auto p-4 sm:p-5">
              <section aria-labelledby="faq-heading">
                <h2 id="faq-heading" className="mb-3 text-sm font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]">Frequently asked questions</h2>
                <div className="mb-3">
                  <label htmlFor="faq-search" className="sr-only">Search FAQs</label>
                  <input
                    id="faq-search"
                    type="search"
                    value={search}
                    onChange={(event) => { setSearch(event.target.value); setExpandedIndex(-1); }}
                    placeholder="Search questions and answers..."
                    className="w-full rounded-lg border border-[var(--color-border)] bg-white px-3 py-2 text-sm text-[var(--color-text-primary)] outline-none focus:border-[var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)]"
                  />
                </div>
                {filteredFaqs.length === 0 && <p role="status" className="py-4 text-sm text-[var(--color-text-secondary)]">No FAQs match your search.</p>}
                <div className="space-y-2">
                  {filteredFaqs.map((faq) => {
                    const { index } = faq;
                    const expanded = expandedIndex === index;
                    const answerId = `faq-answer-${index}`;
                    return (
                      <div key={faq.question} className="overflow-hidden rounded-xl border border-[var(--color-border)] bg-white">
                        <button
                          type="button"
                          onClick={() => setExpandedIndex(expanded ? -1 : index)}
                          className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm font-semibold text-[var(--color-text-primary)] hover:bg-[var(--color-workspace)]/40"
                          aria-expanded={expanded}
                          aria-controls={answerId}
                        >
                          <span>{faq.question}</span>
                          <ChevronDown className={`h-4 w-4 shrink-0 text-[var(--color-primary)] transition-transform ${expanded ? "rotate-180" : ""}`} />
                        </button>
                        {expanded && <p id={answerId} className="border-t border-[var(--color-border)] bg-[var(--color-workspace)]/25 px-4 py-3 text-sm leading-6 text-[var(--color-text-secondary)]">{faq.answer}</p>}
                      </div>
                    );
                  })}
                </div>
              </section>

              <section className="mt-6" aria-labelledby="demo-heading">
                <div className="mb-3 flex items-center gap-2">
                  <PlayCircle className="h-5 w-5 text-[var(--color-primary)]" />
                  <h2 id="demo-heading" className="text-sm font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]">Demo videos</h2>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {DEMO_LINKS.map((demo) => {
                    const Icon = demo.icon || Wrench;
                    return (
                      <button key={demo.title} type="button" onClick={() => setSelectedVideo(demo)} className="group flex items-start gap-3 rounded-xl border border-[var(--color-border)] p-4 text-left hover:border-[var(--color-primary)]/40 hover:shadow-sm">
                        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-[var(--color-primary)]/10 text-[var(--color-primary)]"><Icon className="h-4 w-4" /></span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-1.5 text-sm font-semibold text-[var(--color-text-primary)] group-hover:text-[var(--color-primary)]">{demo.title}<ExternalLink className="h-3.5 w-3.5" /></span>
                          <span className="mt-1 block text-xs leading-5 text-[var(--color-text-secondary)]">{demo.description}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </section>
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
      <VideoDialog video={selectedVideo} onClose={() => setSelectedVideo(null)} />
    </>
  );
}
